/**
 * The /program-details page: renders its sections from a payload through
 * `ProgramSections`, inside minimal chrome.
 */
import { useRef } from 'react'
import { EnrollmentBanners } from '@educator/components/EnrollmentBanners'
import { FooterSection } from '@educator/components/FooterSection'
import { TopNavigation } from '@educator/components/TopNavigation'
import { StickyBottomCta } from '@educator/features/EnrollmentPage/components/StickyBottomCta'
import { useLandingEnrollment } from '@educator/hooks/useLandingEnrollment'
import {
  GOLD_STANDARD_INTEGRATIONS,
  TrackOnMount,
  TrackPageViewed,
  TrackingAttributes,
  educatorPageNames,
} from '@educator/tracking/educatorTracking'
import type { EducatorCohortDetailsResponse } from '@mc/api'
import { ProgramNavBar } from './ProgramNavBar'
import { ProgramSections } from './ProgramSections'
import { programDetailsPageData } from './data/programDetailsPage'
import { orderProgramSections } from './orderProgramSections'

const STICKY_CTA_HERO_CTA = '[data-element="hero-cta"]'
const STICKY_CTA_HIDE_WHEN = ['[data-section="admissions"]']

interface ProgramDetailsPageProps {
  initialIsMobile?: boolean
  /** SSR-prefetched cohort-details to seed the hero strip + CTAs (no flash). */
  initialCohortDetails?: EducatorCohortDetailsResponse
  /** Admin-configured section slugs, in display order. */
  sectionOrder?: string[]
}

export const ProgramDetailsPage = ({
  initialIsMobile,
  initialCohortDetails,
  sectionOrder,
}: ProgramDetailsPageProps) => {
  const { banner } = useLandingEnrollment(initialCohortDetails)
  const navRef = useRef<HTMLDivElement>(null)

  return (
    <TrackingAttributes
      page={educatorPageNames.PROGRAM_DETAILS}
      page_source={educatorPageNames.PROGRAM_DETAILS}
    >
      {/* Page surface is bg-primary (#1c1c1c); each section's own 1380 block
          supplies its fill (the hero's bg-secondary #252525 sits on top). */}
      <div className='tailwind-enabled min-h-screen bg-edu-bg-primary'>
        <TrackPageViewed
          name={educatorPageNames.PROGRAM_DETAILS}
          product_name='educator'
          options={{ integrations: GOLD_STANDARD_INTEGRATIONS }}
        />
        <TrackOnMount event='educatorProductViewed' />
        <TrackOnMount
          event='educatorSignViewed'
          name={educatorPageNames.PROGRAM_DETAILS}
        />
        <TopNavigation
          hideMobileMenu
          pageSource={educatorPageNames.PROGRAM_DETAILS}
        />
        {/* Nav is first in the DOM so mobile tab/reading order matches the
            mobile visual order (nav-above-banner). Desktop uses order-* to
            raise the banner above the nav. md:pt-10 clears the absolute
            desktop utility bar. */}
        <div className='flex flex-col md:pt-10'>
          <div ref={navRef} className='order-1 md:order-2 md:py-8'>
            <ProgramNavBar />
          </div>
          <div className='order-2 md:order-1'>
            <EnrollmentBanners banner={banner} withTopOffset={false} />
          </div>
        </div>
        <div data-testid='page-content'>
          <ProgramSections
            sections={orderProgramSections(
              programDetailsPageData.sections,
              sectionOrder,
            )}
            initialIsMobile={initialIsMobile}
            initialCohortDetails={initialCohortDetails}
          />
        </div>
        <FooterSection margin='prePaywall' />
        <StickyBottomCta
          showAfter={[navRef, STICKY_CTA_HERO_CTA]}
          hideWhen={STICKY_CTA_HIDE_WHEN}
        />
      </div>
    </TrackingAttributes>
  )
}
